import { useEffect, useRef, useState } from 'react'
import './Form.css'
import { useForm } from 'react-hook-form'
import { useFetch } from '../../hooks/usefetch'
import { Box, Checkbox } from '@mui/material'
import 'react-international-phone/style.css'
import ReCAPTCHA from 'react-google-recaptcha-enterprise'
import appConfig from '../../app-config'
import { Disclaimer } from '../Disclaimer'
import { ceremonyMap, flatMap } from '../../lib/ceremonies'
import HuiPhoneInput from './HuiPhoneInput/HuiPhoneInput'
import { parsePhoneNumberFromString } from 'libphonenumber-js'
import HuiToast from './HuiToast/HuiToast'
import HuiSubmitButton from './HuiButton/SubmitButton'
import HuiCancelButton from './HuiButton/CancelButton'
import { useNavigate } from 'react-router-dom'

function MitForm({ isUnsubscribe }: { isUnsubscribe: boolean }) {
  const [userInput, setUserInput] = useState<UserInput>({
    phoneNumber: '+1',
    email: '',
    ceremonies: [],
    captchaToken: null
  })
  const [disclaimerChecked, setDisclaimerChecked] = useState<boolean>(false)

  const yourInfoRef = useRef<HTMLDivElement>(null)
  const chooseCeremonyRef = useRef<HTMLDivElement>(null)
  const recaptchaRef = useRef<ReCAPTCHA>(null)
  const recaptchaWrapperRef = useRef<HTMLDivElement>(null)
  const disclaimerRef = useRef<HTMLDivElement>(null)

  const { loading, subscribe, unsubscribe } = useFetch()
  const [toast, setToast] =
    useState<{ message: string; type: 'success' | 'error' | 'warning' } | null>(
      null
    )
  const [hideToast, setHideToast] = useState<boolean>(false)
  const [phoneInvalid, setPhoneInvalid] = useState<boolean>(false)
  // const [emailInvalid, setEmailInvalid] = useState<boolean>(false)
  const [ceremoniesInvalid, setCeremoniesInvalid] = useState<boolean>(false)
  const [captchaInvalid, setCaptchaInvalid] = useState<boolean>(false)
  const [disclaimerInvalid, setDisclaimerInvalid] = useState<boolean>(false)
  const [checkboxDisabled, setCheckboxDisabled] = useState<boolean>(false)
  const [buttonsDisabled, setButtonsDisabled] = useState<boolean>(false)
  const { handleSubmit, setValue } = useForm<UserInput>({
    defaultValues: Object.create(userInput)
  })
  const navigate = useNavigate()

  const updateUserInput = (value: string, name: string) => {
    setButtonsDisabled(false)
    setUserInput((userInput) => ({
      ...userInput,
      [name]: value
    }))
    if (name === 'phoneNumber' && value === '') {
      updateUserInput('+1', 'phoneNumber')
    }
    setValue(name as keyof UserInput, value)
  }

  const onSubmit = handleSubmit(
    async (e: any) => {
      if (hasErrors() === false) {
        console.log('Sending valid data: ', userInput)
        let status
        if (!isUnsubscribe) status = await subscribe(userInput)
        else status = await unsubscribe(userInput)
        showToast(status)
        recaptchaRef.current?.reset()
      }
    },
    (errors: any, e: any) => {
      hasErrors()
    }
  )

  const validateInputs = (): boolean => {
    var validInputs = 0

    // const emailRegex = /^[\w.-]+@[a-zA-Z\d.-]+\.[a-zA-Z]{2,}$/
    // if (!emailRegex.test(userInput.email)) {
    //   setEmailInvalid(true)
    // } else {
    //   validInputs++
    //   setEmailInvalid(false)
    // }

    // console.log('email invalid', emailInvalid)

    const phoneNumber = parsePhoneNumberFromString(userInput.phoneNumber)
    if (!phoneNumber || !phoneNumber.isValid()) {
      setPhoneInvalid(true)
    } else {
      validInputs++
      setPhoneInvalid(false)
    }
    if (validInputs === 0) {
      scrollToField(yourInfoRef)
      return false
    }
    // setEmailInvalid(false)
    setPhoneInvalid(false)
    return true
  }

  const hasErrors = (): boolean => {
    let errCount = 0

    if (!validateInputs()) {
      errCount++
    }

    if (userInput.ceremonies.length === 0) {
      errCount++
      if (errCount === 1) {
        scrollToField(chooseCeremonyRef)
      }
      setCeremoniesInvalid(true)
    } else {
      setCeremoniesInvalid(false)
    }

    if (!recaptchaRef.current?.getValue()) {
      errCount++
      if (errCount === 1) {
        scrollToField(recaptchaWrapperRef)
      }
      setCaptchaInvalid(true)
    } else {
      setCaptchaInvalid(false)
    }

    if (!isUnsubscribe && !disclaimerChecked) {
      errCount++
      if (errCount === 1) {
        scrollToField(disclaimerRef)
      }
      setDisclaimerInvalid(true)
    } else {
      setDisclaimerInvalid(false)
    }

    if (errCount > 0) {
      setToast({
        message:
          'Oops! Seems like one or more of your information fields are incorrect.',
        type: 'error'
      })
      return true
    }

    return false
  }

  const scrollToField = <T extends HTMLElement>(
    targetRef: React.RefObject<T>
  ) => {
    if (targetRef.current) {
      const top =
        targetRef.current.getBoundingClientRect().top + window.scrollY - 80
      window.scrollTo({ top, behavior: 'smooth' })
    }
  }

  const toggleInput = (item: { id: string; name: string }) => {
    const exists = userInput.ceremonies.some(
      (ceremony) => ceremony.id === item.id
    )

    if (exists) {
      setUserInput({
        ...userInput,
        ceremonies: userInput.ceremonies.filter((item1) => item1.id !== item.id)
      })
    } else {
      setUserInput({
        ...userInput,
        ceremonies: [...userInput.ceremonies, item]
      })
    }

    setButtonsDisabled(false)
    console.log(userInput)
  }

  const showToast = (status: number) => {
    if (status === 201) {
      setUserInput({
        phoneNumber: '+1',
        email: '',
        ceremonies: [],
        captchaToken: null
      })
      setCheckboxDisabled(true)
      setButtonsDisabled(true)
      // setEmailInvalid(false)
      setPhoneInvalid(false)
      navigate('/success')
    }

    if (status === 204) {
      setUserInput({
        phoneNumber: '+1',
        email: '',
        ceremonies: [],
        captchaToken: null
      })
      setCheckboxDisabled(true)
      setButtonsDisabled(true)
      // setEmailInvalid(false)
      setPhoneInvalid(false)
      navigate('/unsubscribe/success')
    }

    if (status === 424) {
      setToast({
        message:
          'You have already subscribed to a ceremony. Please confirm your subscription before subscribing to another ceremony.',
        type: 'error'
      })
    }

    if (status === 500) {
      setToast({
        message: 'Something went wrong. Please try again later.',
        type: 'error'
      })
    }

    if (status === 400) {
      setToast({ message: 'Invalid details. Please try again.', type: 'error' })
    }
  }

  const toUsDate = (dateString: string) => {
    const date = new Date(dateString)
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    })
  }

  useEffect(() => {
    if (toast) {
      const timer = setTimeout(() => {
        setHideToast(true)
        setTimeout(() => {
          setToast(null)
          setHideToast(false)
        }, 500)
      }, 5000)

      return () => clearTimeout(timer)
    }
  }, [toast])

  useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    const name = params.get('ceremony')
    const id = name ? flatMap.get(name) : null
    if (name && id) {
      setUserInput({ ...userInput, ceremonies: [{ name, id }] })
    }
  }, [userInput])

  return (
    <>
      <HuiToast
        toast={toast}
        hideToast={hideToast}
        onClose={() => setToast(null)}
      />
      <form onSubmit={onSubmit} className="Form">
        <div className="form-item">
          <div className="form-subheader" ref={yourInfoRef}>
            Your mobile number
          </div>

          <Box className="two-columns">
            {/* <HuiInputField
              invalid={emailInvalid}
              placeholder="Your personal email address"
              value={userInput.email}
              onChange={updateUserInput}
              name="email"
            /> */}
            <HuiPhoneInput
              invalid={phoneInvalid}
              placeholder="Your phone number"
              value={userInput.phoneNumber}
              onChange={updateUserInput}
              name="phoneNumber"
            />
          </Box>
        </div>
        <div className="form-item">
          <div className="form-subheader" ref={chooseCeremonyRef}>
            {isUnsubscribe
              ? 'Which ceremonies notifications would you like to unsubscribe from?'
              : 'Which ceremonies will you be attending?'}
          </div>

          {!isUnsubscribe && (<div className="form-subtext">
            Select the specific <a target="_blank" rel="noreferrer" href="https://commencement.mit.edu/schedule">2025 MIT Commencement ceremony or ceremonies</a> you plan to attend.
          </div>)}
          {isUnsubscribe && (<div className="form-subtext">
            Select the specific <a target="_blank" rel="noreferrer" href="https://commencement.mit.edu/schedule">2025 MIT Commencement ceremony or ceremonies</a> for which you no longer wish to receive notifications.
          </div>)}
          {ceremoniesInvalid && (
            <div className="error-text">
              Please select at least one ceremony
            </div>
          )}
          <div className="ceremony-wrapper">
            {isUnsubscribe && (<div>
              <p className="ceremony-date"></p>
              <div className="date-group">

                <div
                  className="checkbox-wrapper"
                  onClick={() => toggleInput({name: 'Unsubscribe from all text notifications', id:'-1'})}
                  key={-1}
                >
                  <div className="ceremony-text">Unsubscribe from all text notifications</div>
                  <Checkbox
                    sx={{
                      color: 'rgba(0, 0, 0, 0.25)',
                      '&.Mui-checked': {
                        color: 'black' // Checked border color
                      }
                    }}
                    style={{
                      padding: '0'
                    }}
                    onChange={(event, checked) => {
                      setButtonsDisabled(false)
                      toggleInput({name:'Unsubscribe from all text notifications', id:'-1'})
                    }}
                    checked={userInput.ceremonies.some((item) => {
                      return item.id === '-1'
                    })}
                    disableRipple
                  />
                </div>
              </div>
            </div>)}
            {Array.from(ceremonyMap.entries()).map(([date, ceremonies]) => (
              <div key={date}>
                <p className="ceremony-date">{toUsDate(date)}</p>
                <div className="date-group">
                  {Array.from(ceremonies.entries()).map(([name, id]) => (
                    <div
                      className="checkbox-wrapper"
                      onClick={() => toggleInput({ name, id })}
                      key={id}
                    >
                      <div className="ceremony-text">{name}</div>
                      <Checkbox
                        sx={{
                          color: 'rgba(0, 0, 0, 0.25)',
                          '&.Mui-checked': {
                            color: 'black' // Checked border color
                          }
                        }}
                        style={{
                          padding: '0'
                        }}
                        onChange={(event, checked) => {
                          setButtonsDisabled(false)
                          toggleInput({ name, id })
                        }}
                        checked={userInput.ceremonies.some((item) => {
                          return item.id === id
                        })}
                        disableRipple
                      />
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="form-item">
          {captchaInvalid && (
            <div className="error-text">Please complete the recaptcha</div>
          )}
          <div className="captcha-wrapper" ref={recaptchaWrapperRef}>
            <ReCAPTCHA
              ref={recaptchaRef}
              asyncScriptOnLoad={() => {}}
              sitekey={appConfig.recaptchaSiteKey}
              onChange={(token: string | null) => {
                setButtonsDisabled(false)
                if (token) {
                  updateUserInput(token, 'captchaToken')
                }
              }}
            />
          </div>
        </div>

        {!isUnsubscribe && (
          <div className="form-item">
            {disclaimerInvalid && (
              <div className="error-text">
                Please read and accept the privacy policy
              </div>
            )}
            <div className="disclaimer-wrapper" ref={disclaimerRef}>
              <Checkbox
                sx={{
                  color: 'rgba(0, 0, 0, 0.25)',
                  '&.Mui-checked': {
                    color: 'black'
                  },
                  '&.Mui-disabled': {
                    color: 'rgba(0, 0, 0, 0.25)'
                  }
                }}
                style={{
                  padding: '0'
                }}
                onChange={() => setDisclaimerChecked(!disclaimerChecked)}
                checked={disclaimerChecked}
                disableRipple
                disabled={checkboxDisabled}
              />
              <Disclaimer />
            </div>
          </div>
        )}

        <div className="button-wrapper">
          <HuiCancelButton
            text="Cancel"
            onClick={() => {
              setUserInput({
                captchaToken: null,
                ceremonies: [],
                email: '',
                phoneNumber: '+1'
              })
              setDisclaimerChecked(false)
            }}
            disabled={buttonsDisabled}
          />
          <HuiSubmitButton
            loading={loading}
            text={isUnsubscribe ? 'Unsubscribe' : 'Submit'}
            onClick={onSubmit}
            disabled={buttonsDisabled}
          />
        </div>
      </form>
    </>
  )
}

export default MitForm
